// frontend/src/pages/UserManualPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Download, Search, ChevronRight,
  CheckCircle2, AlertTriangle, ShieldCheck, Layers, FileText,
  Camera, Settings2, BarChart3, Globe, User, LifeBuoy, BookMarked,
  Ship, Truck, Package, Zap, Info, Disc3
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import UserManualTurkish from './UserManualTurkish';

// ── Chapter metadata ──────────────────────────────────────────────────────────
const chapters = [
  { id: 'intro',     icon: BookOpen,    label: '1. Introduction' },
  { id: 'auth',      icon: ShieldCheck, label: '2. Authentication' },
  { id: 'interface', icon: Layers,      label: '3. Interface Tour' },
  { id: 'modes',     icon: Ship,        label: '4. Transport Modes & Units' },
  { id: 'cargo',     icon: Package,     label: '5. Cargo & CoG' },
  { id: 'twins',     icon: BarChart3,   label: '6. 3D Visualizations' },
  { id: 'securing',  icon: Settings2,   label: '7. Securing Elements' },
  { id: 'calcmodes', icon: Zap,         label: '8. Optimizer vs Manual' },
  { id: 'scenarios', icon: Truck,       label: '9. Scenarios' },
  { id: 'results',   icon: CheckCircle2,label: '10. Results' },
  { id: 'pdf',       icon: FileText,    label: '11. PDF Reports' },
  { id: 'surveys',   icon: Camera,      label: '12. Survey & Photos' },
  { id: 'steelcoil', icon: Disc3,       label: '13. Steel Coil Module' },
  { id: 'projects',  icon: BookMarked,  label: '14. Validation & Edge Cases' },
  { id: 'profile',   icon: User,        label: '15. Profile & Settings' },
  { id: 'faq',       icon: LifeBuoy,    label: 'FAQ & Troubleshooting' },
];

// ── Small reusable components ─────────────────────────────────────────────────
const Callout = ({ type, children }: { type: 'note'|'tip'|'warning'|'important', children: React.ReactNode }) => {
  const styles = {
    note:      { bg: 'bg-blue-50 dark:bg-blue-950/30',   border: 'border-blue-300 dark:border-blue-700',   icon: Info,          iconColor: 'text-blue-600',   label: 'Note' },
    tip:       { bg: 'bg-green-50 dark:bg-green-950/30', border: 'border-green-300 dark:border-green-700', icon: CheckCircle2,  iconColor: 'text-green-600',  label: 'Tip' },
    warning:   { bg: 'bg-yellow-50 dark:bg-yellow-950/30',border:'border-yellow-300 dark:border-yellow-700',icon: AlertTriangle, iconColor:'text-yellow-600',  label:'Warning' },
    important: { bg: 'bg-orange-50 dark:bg-orange-950/30',border:'border-orange-300 dark:border-orange-700',icon: ShieldCheck,   iconColor:'text-orange-600',  label:'Important' },
  }[type];
  const Icon = styles.icon;
  return (
    <div className={`flex gap-3 p-4 rounded-xl border ${styles.bg} ${styles.border} my-4`}>
      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${styles.iconColor}`} />
      <div className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">{children}</div>
    </div>
  );
};

const SectionTitle = ({ id, icon: Icon, children }: { id: string; icon: any; children: React.ReactNode }) => (
  <h2 id={id} className="flex items-center gap-3 text-2xl font-black text-slate-900 dark:text-white border-b border-slate-100 dark:border-white/10 pb-3 mb-5 scroll-mt-28">
    <span className="p-2 bg-orange-600/10 text-orange-600 rounded-xl"><Icon className="w-6 h-6" /></span>
    {children}
  </h2>
);

const SubTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mt-6 mb-3">{children}</h3>
);

const ManualTable = ({ headers, rows }: { headers: string[]; rows: string[][] }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-white/10 my-4">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="bg-slate-100 dark:bg-white/5 text-left">
          {headers.map((h, i) => <th key={i} className="px-4 py-3 font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className={ri % 2 === 0 ? 'bg-white dark:bg-transparent' : 'bg-slate-50/50 dark:bg-white/[0.02]'}>
            {row.map((cell, ci) => <td key={ci} className="px-4 py-2.5 text-slate-700 dark:text-slate-300 border-t border-slate-100 dark:border-white/5">{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Screenshot = ({ src, alt, caption }: { src: string; alt: string; caption?: string }) => (
  <figure className="my-6">
    <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-white/10 shadow-lg bg-slate-100 dark:bg-black/20">
      <img src={src} alt={alt} className="w-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display='none'; }} />
    </div>
    {caption && <figcaption className="text-center text-xs text-slate-500 mt-2 italic">{caption}</figcaption>}
  </figure>
);

const Para = ({ children }: { children: React.ReactNode }) => (
  <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-3">{children}</p>
);

const Ul = ({ items }: { items: (string | React.ReactNode)[] }) => (
  <ul className="space-y-1.5 mb-4">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
        <ChevronRight className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const EnglishUserManualPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('intro');
  const mainRef = useRef<HTMLDivElement>(null);

  // Scroll-spy
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );
    document.querySelectorAll('section[id]').forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const filteredChapters = chapters.filter(c =>
    searchQuery === '' || c.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const scrollTo = (id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0B0F17] text-slate-900 dark:text-white transition-colors">
      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-[#0D111C]/90 backdrop-blur-md border-b border-slate-200 dark:border-white/10 px-4 lg:px-8 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-slate-300 transition-colors" title="Back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-orange-600/10 text-orange-600 rounded-xl">
                <BookOpen className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-lg font-black tracking-tight">User Manual & Operational Guide</h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">CTU Code (IMO/ILO/UNECE) | Version 2.4.2</p>
              </div>
            </div>
          </div>
          
        </div>
      </header>

      {/* ── Body ── */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 flex flex-col lg:flex-row gap-8">

        {/* ── Sidebar Nav ── */}
        <aside className="w-full lg:w-64 xl:w-72 shrink-0">
          <div className="sticky top-24 bg-white dark:bg-[#131B2E] rounded-2xl p-4 border border-slate-200 dark:border-white/10 shadow-sm">
            <div className="relative mb-4">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" placeholder="Search chapters…" value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-slate-100 dark:bg-white/5 rounded-xl border border-transparent focus:border-orange-500 outline-none transition-all"
              />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-2 mb-2">Contents</p>
            <nav className="space-y-0.5 max-h-[70vh] overflow-y-auto">
              {filteredChapters.map(({ id, icon: Icon, label }) => (
                <button key={id} onClick={() => scrollTo(id)}
                  className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                    activeSection === id
                      ? 'bg-orange-50 dark:bg-orange-600/10 text-orange-600 dark:text-orange-400'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5'
                  }`}>
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  {label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* ── Article ── */}
        <main ref={mainRef} className="flex-1 min-w-0 bg-white dark:bg-[#131B2E] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm p-6 lg:p-10 space-y-16">

          {/* 1 — Introduction */}
          <section id="intro">
            <SectionTitle id="intro" icon={BookOpen}>1. Introduction & System Overview</SectionTitle>
            <Callout type="important">
              <strong>Calculation Basis:</strong> This platform uses a <strong>simplified securing assessment informed by the IMO/ILO/UNECE CTU Code 2014 and its Informative Material</strong>. It is not a certificate of compliance with EN 12195-1, IMO CSS Code Annex 13, or UIC rules. Results apply only to the entered data and stated assumptions and must be verified by a qualified cargo securing professional, surveyor, and the relevant carrier before transport.
            </Callout>
            <Para>The <strong>Lashing Calculator SaaS Platform</strong> is a professional-grade engineering tool for calculating cargo securing requirements across road, rail, and sea transport. It handles force calculations, securing element optimization, 3D digital twin visualizations, PDF audit certificate generation, and a photo-evidence survey workflow.</Para>
            <SubTitle>Who Is This For?</SubTitle>
            <ManualTable
              headers={['User Type', 'Primary Use']}
              rows={[
                ['Marine Surveyors', 'Verify on-site lashing plans against calculated requirements'],
                ['Cargo Securing Companies', 'Generate certified lashing plans for road and sea'],
                ['Port Terminal Operators', 'Standardize cargo securing checklists'],
                ['Freight Forwarders', 'Confirm compliance before booking vessel space'],
                ['Insurance Underwriters', 'Obtain timestamped calculation records for claims'],
              ]}
            />
          </section>

          {/* 2 — Auth */}
          <section id="auth">
            <SectionTitle id="auth" icon={ShieldCheck}>2. Getting Started & Authentication</SectionTitle>
            <Para>Navigate to <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-white/10 rounded font-mono text-orange-600 text-xs">/login</code> and enter your registered email and password.</Para>
            <Screenshot src="/manual/01_login_page.png" alt="Login Page" caption="Login page — enter your corporate email and password" />
            <Callout type="note">
              The platform enforces a <strong>Multi-Session Guard</strong>. If your account is authenticated from a second device simultaneously, the earlier session is automatically terminated for security.
            </Callout>
            <SubTitle>User Roles</SubTitle>
            <ManualTable
              headers={['Role', 'Access']}
              rows={[
                ['Standard User', 'Calculator, Surveys, Profile, Manual'],
                ['Surveyor', 'Surveyor dashboard, survey report editor'],
                ['Admin', 'All above + admin dashboard, advanced rope display mode'],
              ]}
            />
          </section>

          {/* 3 — Interface */}
          <section id="interface">
            <SectionTitle id="interface" icon={Layers}>3. Calculator Interface Tour</SectionTitle>
            <Screenshot src="/manual/en/01_calculator_overview.png" alt="Configured calculator overview" caption="A fully configured calculator workspace with transport, cargo and securing selections" />
            <SubTitle>Header Bar</SubTitle>
            <Ul items={[
              <><strong>Language Switcher (🌐)</strong>: EN, TR, ES, DE, ZH, JA, KO</>,
              <><strong>Theme Toggle (🌙/☀️)</strong>: Light and dark mode</>,
              <><strong>📖 Manual</strong>: Opens this guide</>,
              <><strong>📄 Reports</strong>: Navigate to your archive at /surveys</>,
              <><strong>👤 Profile</strong>: Account and settings</>,
              <><strong>Sign Out</strong>: Securely terminate your session</>,
            ]} />
            <SubTitle>Three Main Panels</SubTitle>
            <ManualTable
              headers={['Panel', 'Location', 'Contains']}
              rows={[
                ['Inputs', 'Left', 'Transport mode, unit, weight, dimensions, CoG, friction material'],
                ['Securing Elements', 'Center (Sidebar)', 'Calculation mode toggle, rope selection, dunnage, stoppers, Calculate button'],
                ['Results & 3D Twins', 'Right', '3D CoG twin, 3D lashing diagram, force results table'],
              ]}
            />
          </section>

          {/* 4 — Modes */}
          <section id="modes">
            <SectionTitle id="modes" icon={Ship}>4. Transport Modes & Transport Units</SectionTitle>
            <SubTitle>4.1 — Transport Modes</SubTitle>
            <Para>Select the mode that matches your cargo's journey. The mode determines the acceleration coefficients used in force calculations.</Para>
            <ManualTable
              headers={['Mode', 'Description', 'Forward', 'Backward', 'Lateral']}
              rows={[
                ['Road', 'Road transport only', '0.8 g', '0.5 g', '0.5 g'],
                ['Rail', 'Rail — shunting forces', '1.0 g', '1.0 g', '0.5 g'],
                ['Sea A', 'Sheltered / calm waters', '0.3 g', '0.3 g', '0.5 g'],
                ['Sea B', 'Normal sea conditions', '0.3 g', '0.3 g', '0.7 g'],
                ['Sea C', 'Severe sea conditions', '0.4 g', '0.4 g', '0.8 g'],
                ['Road + Sea B', 'Road leg then sea', '0.8 g', '0.5 g', '0.7 g'],
                ['Road + Rail + Sea C', 'All three modes', '1.0 g', '1.0 g', '0.8 g'],
              ]}
            />
            <Callout type="tip">For multi-modal journeys, select the combined mode (e.g. <em>Road + Sea C</em>) to apply the most conservative acceleration envelope across all legs.</Callout>
            <SubTitle>4.2 — Transport Units</SubTitle>
            <Callout type="important">The Transport Unit selector is <strong>filtered by the selected Mode</strong>. Greyed-out units are incompatible with the current mode. For example, Mafi trailers are only available under Sea modes.</Callout>
            <ManualTable
              headers={['Unit', 'Max Cargo', 'Direct MSL', 'Loop MSL', 'Modes']}
              rows={[
                ['20ft Container', '28 MT', '100 kN', '100 kN', 'Sea, Road, Rail'],
                ['40ft Container', '26.5 MT', '100 kN', '100 kN', 'Sea, Road, Rail'],
                ['40ft Flatrack', '40 MT', '150 kN', '100 kN', 'Sea, Road, Rail'],
                ['Breakbulk', '600 MT', '300 kN', '200 kN', 'All'],
                ['Lowbed Trailer', '100 MT', '200 kN', '150 kN', 'Road only'],
                ['Mafi Roll Trailer', '150 MT', '200 kN', '150 kN', 'Sea only'],
                ['Vessel Hold', '600 MT', '500 kN', '300 kN', 'Sea only'],
                ['Truck Trailer Standard', '24 MT', '100 kN', '80 kN', 'Road only'],
                ['Truck Trailer Mega', '26 MT', '100 kN', '80 kN', 'Road only'],
              ]}
            />
          </section>

          {/* 5 — Cargo & CoG */}
          <section id="cargo">
            <SectionTitle id="cargo" icon={Package}>5. Cargo Specifications — Weight, Dimensions & CoG</SectionTitle>
            <SubTitle>5.1 — Weight & Dimensions</SubTitle>
            <ManualTable
              headers={['Field', 'Description', 'Unit']}
              rows={[
                ['Gross Weight', 'Total cargo mass (max 600 MT)', 'Metric Tons'],
                ['Length', 'Cargo length along longitudinal (fore-aft) axis', 'metres'],
                ['Width', 'Cargo width across transverse axis', 'metres'],
                ['Height', 'Cargo height from contact surface', 'metres'],
              ]}
            />
            <SubTitle>5.2 — Center of Gravity (CoG) Coordinates</SubTitle>
            <ManualTable
              headers={['Field', 'Axis', 'Description']}
              rows={[
                ['CoG X (b_boy)', 'Longitudinal', 'Distance of CoG from geometric center, forward (+) or aft (-). Enter 0 if centered.'],
                ['CoG Y (b_en)', 'Transverse', 'Distance of CoG from center, starboard (+) or port (-). Enter 0 if centered.'],
                ['CoG Z (d)', 'Vertical', 'Height of CoG above the contact surface. CRITICAL — high CoG generates large tipping moments.'],
              ]}
            />
            <Callout type="tip">For homogeneous rectangular cargo, CoG Z = Height ÷ 2. For machinery with a heavy base, CoG Z is lower. Use the manufacturer's certificate for declared CoG height.</Callout>
            <SubTitle>5.3 — Friction Surface</SubTitle>
            <ManualTable
              headers={['Material', 'μ', 'Typical Use']}
              rows={[
                ['Metal / Metal', '0.20', 'Steel cargo on bare container floor'],
                ['Metal / Wood', '0.40', 'Steel cargo on timber dunnage or wooden floor'],
                ['Wood / Wood', '0.45', 'Wooden crates on wooden platform'],
                ['Anti-Slip Mat (ASM)', '0.60', 'Any cargo on rubber anti-slip matting'],
              ]}
            />
          </section>

          {/* 6 — 3D Twins */}
          <section id="twins">
            <SectionTitle id="twins" icon={BarChart3}>6. 3D Visualizations — CoG Twin & Lashing Diagram</SectionTitle>
            <SubTitle>6.1 — 3D Center of Gravity Digital Twin</SubTitle>
            <Para>The 3D CoG Twin updates <strong>live as you type</strong> — no need to click Calculate. It shows a 3D box representing your cargo with a red dot marking the CoG position.</Para>
            <Screenshot src="/manual/en/03_cog_centered_3d.png" alt="3D CoG Overview" caption="Centered CoG with cargo dimensions and directional axes" />
            <Screenshot src="/manual/en/04_cog_high_edge_case.png" alt="High CoG edge case" caption="High CoG edge case — the red marker approaches the upper cargo boundary and tipping sensitivity increases" />
            <Screenshot src="/manual/en/05_cog_off_center_edge_case.png" alt="Off-center CoG edge case" caption="Longitudinally and laterally offset CoG creating asymmetric directional behavior" />
            <Ul items={[
              <><strong>Normal (gray/blue)</strong>: CoG is in a safe range — tipping risk is low</>,
              <><strong>Orange warning</strong>: CoG height exceeds Width/2 — increased tipping moment</>,
              <><strong>Red warning</strong>: Critically high or off-center CoG — additional securing strongly required</>,
              <><strong>Interaction</strong>: Click and drag to rotate the 3D model and inspect from any angle</>,
            ]} />
            <SubTitle>6.2 — 3D Lashing Arrangement Diagram</SubTitle>
            <Para>After clicking Calculate, the 3D Lashing Diagram renders showing all securing elements:</Para>
            <Screenshot src="/manual/en/07_3d_lashing_results.png" alt="3D Lashing Diagram" caption="3D Lashing Diagram — computed direct lashings, loops and support elements" />
            <Ul items={[
              'Direct lashing ropes: lines from cargo connection points to deck rings, colored by direction',
              'Loop lashings: ropes arching over the cargo top in a complete loop',
              'Wooden dunnage blocks: rectangular shapes positioned against cargo faces',
              'Steel stoppers: bracket icons at the forward and aft ends of the cargo',
            ]} />
          </section>

          {/* 7 — Securing */}
          <section id="securing">
            <SectionTitle id="securing" icon={Settings2}>7. Securing Elements Configuration</SectionTitle>
            <SubTitle>7.1 — Direct Lashing Ropes</SubTitle>
            <Para>Direct lashings run from cargo attachment points diagonally to deck lashing rings. In Optimizer mode, the system determines the minimum count. In Manual mode, you specify exact rope types, quantities, position (above/below cargo), and angle per direction.</Para>
            <SubTitle>7.2 — Loop Lashing</SubTitle>
            <Para>A loop lashing passes completely over the cargo, forming a loop. It adds a vertical friction component to the securing force.</Para>
            <Callout type="warning">Loop lashing is <strong>automatically disabled</strong> when Oversized Width is enabled — cargo overhanging the transport unit sides makes loop lashing physically ineffective.</Callout>
            <SubTitle>7.3 — Wooden Dunnage (Takoz)</SubTitle>
            <Ul items={[
              'Toggle the Wooden Dunnage checkbox to enable',
              'Configure Width, Height, and Count of dunnage blocks',
              'Available for all units except Vessel',
              'Adds a mechanical blocking force (SE — Securing Element) proportional to cargo weight × 0.3 coefficient',
            ]} />
            <SubTitle>7.4 — Steel Stoppers (Vessel / Mafi Only)</SubTitle>
            <Para>Steel stoppers are heavy structural brackets providing 100 kN of shear resistance per stopper. Configure the count per direction:</Para>
            <ManualTable
              headers={['Direction', 'Field', 'Effect']}
              rows={[
                ['Forward', 'stopper_adet_forward', 'Blocks forward sliding (e.g. under braking)'],
                ['Backward', 'stopper_adet_backward', 'Blocks backward sliding (e.g. acceleration)'],
                ['Right', 'stopper_adet_right', 'Blocks rightward sliding (rolling)'],
                ['Left', 'stopper_adet_left', 'Blocks leftward sliding (rolling)'],
              ]}
            />
            <Callout type="important">Stoppers are restricted to <strong>Vessel</strong> and <strong>Mafi</strong> transport units in Sea mode combinations only.</Callout>
            <Screenshot src="/manual/en/09_vessel_stopper_configuration.png" alt="Vessel stopper configuration" caption="Sea C vessel scenario with directional stopper quantities" />
            <SubTitle>7.5 — Oversized Width Cargo Flag</SubTitle>
            <Para>Check this when cargo width exceeds the transport unit's interior (cargo overhangs flatrack sides). Effects:</Para>
            <Ul items={[
              'Loop lashing is automatically disabled',
              'Breakbulk layout algorithm activates — provides recommended lashing point count, left/right rope distribution, and flatrack orientation guidance',
              'A separate breakbulk plan diagram is embedded in the PDF report',
            ]} />
            <Screenshot src="/manual/en/11_oversized_width_dunnage_edge_case.png" alt="Oversized flatrack width" caption="Oversized flatrack cargo — side dunnage is disabled because the cargo overhangs the unit" />
          </section>

          {/* 8 — Calc Modes */}
          <section id="calcmodes">
            <SectionTitle id="calcmodes" icon={Zap}>8. Calculation Modes: Optimizer vs Manual</SectionTitle>
            <SubTitle>8.1 — Optimizer (Default)</SubTitle>
            <Para>Automatically solves for the minimum required lashing ropes per direction to satisfy safety equilibrium in all four directions simultaneously.</Para>
            <SubTitle>8.2 — Manual (Calculator)</SubTitle>
            <Para>You specify the exact arrangement. The system verifies compliance. Allows mixed rope types, custom angles, and multiple rope entries per direction.</Para>
            <SubTitle>8.3 — Optimizer → Manual Transfer Workflow</SubTitle>
            <Ul items={[
              <><strong>Step 1</strong>: Run Optimizer — get minimum required rope counts per direction</>,
              <><strong>Step 2</strong>: Switch to Manual mode. A dialog asks to transfer Optimizer results into Manual fields</>,
              <><strong>Step 3</strong>: Adjust — add ropes, change rope types, modify angles to match on-site equipment</>,
              <><strong>Step 4</strong>: Re-calculate — verify the adjusted arrangement still meets safety margins</>,
            ]} />
            <Callout type="tip">Use the transfer workflow when on-site inventory doesn't match the Optimizer's recommendation. Reduce ropes in one direction and verify the total still passes.</Callout>
            <Screenshot src="/manual/en/08_manual_direct_and_loop.png" alt="Manual lashing configuration" caption="Manual direct-lashing rows and loop-lashing quantities after optimizer transfer" />
          </section>

          {/* 9 — Scenarios */}
          <section id="scenarios">
            <SectionTitle id="scenarios" icon={Truck}>9. Step-by-Step Scenarios</SectionTitle>
            <SubTitle>Scenario 1 — Standard Road Transport (Truck Trailer)</SubTitle>
            <ManualTable
              headers={['Parameter', 'Value']}
              rows={[
                ['Transport Mode', 'Road'],
                ['Transport Unit', 'Truck Trailer Standard'],
                ['Cargo Weight', '15 MT'],
                ['Dimensions (L×W×H)', '3.0 m × 2.0 m × 2.0 m'],
                ['CoG Z', '1.0 m'],
                ['Friction', 'Metal / Wood (μ = 0.40)'],
                ['Loop Lashing', 'Enabled — 1× webbing strap'],
                ['Mode', 'Optimizer'],
              ]}
            />
            <Screenshot src="/manual/en/06_optimizer_results.png" alt="Scenario 1 Results" caption="Optimizer results — all four directional sliding and tipping checks pass" />

            <SubTitle>Scenario 2 — Vessel with Steel Stoppers (Sea C)</SubTitle>
            <ManualTable
              headers={['Parameter', 'Value']}
              rows={[
                ['Transport Mode', 'Sea C'],
                ['Transport Unit', 'Vessel Hold'],
                ['Cargo Weight', '60 MT'],
                ['Dimensions', '5.0 m × 3.5 m × 3.0 m'],
                ['CoG Z', '1.8 m'],
                ['Friction', 'Metal / Metal (μ = 0.20)'],
                ['Stoppers', '2 Forward, 2 Backward, 1 Right, 1 Left (200 kN + 200 kN + 100 kN + 100 kN)'],
                ['Mode', 'Optimizer'],
              ]}
            />
            <SubTitle>Scenario 4 — Breakbulk Dimension Overflow</SubTitle>
            <Para>When cargo length equals or exceeds the selected flatrack length, calculation is blocked. Correct the flatrack type, orientation or count before continuing.</Para>
            <Screenshot src="/manual/en/10_breakbulk_dimension_edge_case.png" alt="Breakbulk dimension overflow" caption="Blocking validation for a 10 m cargo on a selected 20 ft flatrack arrangement" />

            <SubTitle>Scenario 3 — Flatrack Breakbulk, Oversized Width (Sea C)</SubTitle>
            <ManualTable
              headers={['Parameter', 'Value']}
              rows={[
                ['Transport Mode', 'Sea C'],
                ['Transport Unit', '40ft Flatrack'],
                ['Cargo Weight', '30 MT'],
                ['Dimensions', '6.0 m × 3.0 m × 2.5 m'],
                ['Friction', 'Metal / Metal (μ = 0.20)'],
                ['Oversized Width', 'Checked — loop lashing auto-disabled'],
                ['Mode', 'Optimizer → Manual Transfer'],
              ]}
            />
          </section>

          {/* 10 — Results */}
          <section id="results">
            <SectionTitle id="results" icon={CheckCircle2}>10. Interpreting Calculation Results</SectionTitle>
            <SubTitle>10.1 — Results Table Columns</SubTitle>
            <ManualTable
              headers={['Column', 'Symbol', 'Description']}
              rows={[
                ['Sliding Force', 'F_sl', 'Inertial force trying to slide cargo in this direction (kN)'],
                ['Tipping Moment', 'F_tip', 'Rotational tipping moment around cargo edge (kN·m)'],
                ['Friction Force', 'F_f', 'Passive friction from cargo weight × μ (kN)'],
                ['Securing Element', 'F_SE', 'Resistance from dunnage blocking or stoppers (kN)'],
                ['Loop Lashing', 'F_L', 'Vertical friction contribution from loop ropes (kN)'],
                ['Direct Lashing', 'F_DL', 'Resistance from direct rope attachments (kN)'],
                ['Net Sliding', 'F_net_sl', 'F_f + F_SE + F_L + F_DL − F_sl — must be ≥ 0'],
                ['Net Tipping', 'F_net_tip', 'Stabilizing moment − tipping moment — must be ≥ 0'],
              ]}
            />
            <Screenshot src="/manual/en/06_optimizer_results.png" alt="Results Table" caption="Directional sliding and tipping safety factors and status indicators" />
            <SubTitle>10.2 — Force Utilization Bar</SubTitle>
            <Ul items={[
              <><strong>Green (&lt;70%)</strong>: Comfortable safety margin</>,
              <><strong>Yellow (70–90%)</strong>: Approaching limit — acceptable but monitor</>,
              <><strong>Red (&gt;90%)</strong>: Near limit — consider adding one more rope</>,
            ]} />
          </section>

          {/* 11 — PDF */}
          <section id="pdf">
            <SectionTitle id="pdf" icon={FileText}>11. PDF Audit Reports & Certificate Archiving</SectionTitle>
            <Para>After a successful calculation, click <strong>Download PDF</strong> in the sidebar. The backend generates a multi-page audit certificate:</Para>
            <ManualTable
              headers={['Page', 'Contents']}
              rows={[
                ['Page 1', 'Report date, tamper-proof timestamp, transport mode/unit, cargo weight, dimensions, CoG, friction material'],
                ['Page 2', 'Acceleration coefficients applied, full force results table (all 8 columns, all 4 directions), pass/fail status'],
                ['Page 3', '2D overhead SVG lashing diagram, rope types and MSL values, dunnage/stopper config'],
                ['Page 4', 'CTU Code methodology disclaimer, verification statement'],
              ]}
            />
            <Callout type="note">All PDF reports are archived in <strong>/surveys</strong>. You can re-download any past report at any time.</Callout>
            <Screenshot src="/manual/en/12_pdf_project_description.png" alt="PDF project description" caption="Optional project description added to the generated report" />
            <Screenshot src="/manual/en/13_pdf_report_viewer.png" alt="PDF report viewer" caption="Generated calculation report with parameters, material usage and verification results" />
            <Screenshot src="/manual/en/14_saved_pdf_reports.png" alt="Saved PDF reports" caption="Saved report archive with preview, download and delete actions" />
          </section>

          {/* 12 — Surveys */}
          <section id="surveys">
            <SectionTitle id="surveys" icon={Camera}>12. Surveys & Guest Photo Inspection Module</SectionTitle>
            <Para>Field crews can upload on-site cargo photos via a unique link — <strong>no account required</strong>. The photos are embedded in the final PDF audit certificate.</Para>
            <SubTitle>12.1 — Generating the Guest Upload Link</SubTitle>
            <Ul items={[
              'Navigate to /surveys (Reports icon in header)',
              'Find the calculation report you want field photos for',
              'Click 🔗 Copy Link — the unique guest URL is copied to your clipboard',
              'Send this URL to the field crew via WhatsApp, SMS, or email',
            ]} />
            <Screenshot src="/manual/en/16_field_reports_and_photo_workflow.png" alt="Surveys List" caption="Field Reports — upload link, manual photo upload, surveyor assignment and summary PDF actions" />
            <SubTitle>12.2 — Guest Upload Page (12 Photo Slots)</SubTitle>
            <Para>The field crew opens the link on their phone and sees a report summary plus 12 categorized photo slots:</Para>
            <ManualTable
              headers={['Slot', 'Category', 'What to Photograph']}
              rows={[
                ['1', 'Floor', 'Container/trailer floor condition before loading'],
                ['2', 'Cargo', 'Full cargo overview after loading'],
                ['3', 'Front-Right', 'Lashing ropes from front-right angle'],
                ['4', 'Front-Left', 'Lashing ropes from front-left angle'],
                ['5', 'Rear-Right', 'Lashing ropes from rear-right angle'],
                ['6', 'Rear-Left', 'Lashing ropes from rear-left angle'],
                ['7', 'Plate', 'Cargo identification plate / marking'],
                ['8', 'Seal', 'Container or trailer seal number'],
                ['9', 'Overall', 'Full exterior view of vehicle/container'],
                ['10', 'Other 1', 'Additional evidence photo'],
                ['11', 'Other 2', 'Additional evidence photo'],
                ['12', 'Other 3', 'Additional evidence photo'],
              ]}
            />
            <Callout type="note"><strong>HEIC Auto-Conversion:</strong> Apple iPhone photos are automatically converted from HEIC to JPEG on the device before upload. <strong>Auto-Compression:</strong> All photos are compressed to ≤2MB for reliable mobile upload.</Callout>
            <SubTitle>12.3 — Report Download Types</SubTitle>
            <ManualTable
              headers={['Button', 'File Name', 'Contents']}
              rows={[
                ['Download PDF', 'Lashing_Plan_{id}.pdf', 'Calculation-only PDF — no photos'],
                ['Download with Photos', '{id}_w_pictures.pdf', 'Complete PDF with all 12 embedded survey photos'],
              ]}
            />
          </section>

          {/* 13 — Steel Coil Module */}
          <section id="steelcoil">
            <SectionTitle id="steelcoil" icon={Disc3}>13. Steel Coil Assessment & Lashing Module</SectionTitle>
            <Para>The dedicated Steel Coil Assessment module evaluates cargo securing for steel coils according to CTU Code 2014 and IMO MSC.1/Circ.1497 standards.</Para>
            <SubTitle>13.1 — Key Assessment Features</SubTitle>
            <Ul items={[
              'Coil Dimensions & Weight: Enter outer diameter, inner diameter, coil width, and total weight.',
              'Roll Orientation: Choose eye-to-side/eye-to-front (parallel to travel) or eye-to-sky (vertical/transverse).',
              'Bedding & Wedge Geometry: Define wooden bedding dimensions, wedge angles (35°-45°), and wedge height.',
              'Structural Capacity Verification: Calculate capacity limiters between cross-sectional strength and attachment points.',
              '2D SVG & 3D Three.js Visualizer: Inspect real-time 3D scenes and 2D technical drawings of roll, bedding, and lashings.',
              '2-Page Isometric PDF Audit Report: Download a 2-page report with complete step-by-step calculation trace and isometric diagrams.',
            ]} />
            <Callout type="important">Friction coefficients and wedge engagement angles directly control tipping stability safety factors. Specialist verification is recommended for high-weight coils.</Callout>
          </section>

          {/* 14 — Validation and edge cases */}
          <section id="projects">
            <SectionTitle id="projects" icon={BookMarked}>14. Validation Rules & Edge-Case Management</SectionTitle>
            <ManualTable
              headers={['Edge Case', 'System Behavior', 'Required Action']}
              rows={[
                ['Transport mode missing', 'Transport unit remains restricted and calculation cannot start', 'Select the mode covering every journey leg first'],
                ['Zero/invalid cargo value', 'Required-field validation blocks calculation', 'Enter positive values using the displayed units'],
                ['CoG outside cargo bounds', 'Input is physically invalid or produces unreliable behavior', 'Verify coordinate origin, axis and units'],
                ['Oversized flatrack width', 'Side dunnage is disabled and loop lashing may be unsuitable', 'Review overhang geometry and direct-lashing plan'],
                ['Breakbulk length overflow', 'A blocking warning is shown', 'Change flatrack type, orientation or count'],
                ['Stopper enabled with zero quantity', 'Calculation stops at stopper validation', 'Enter a practical directional stopper quantity'],
                ['Input changed after calculation', 'Previous results and PDF action are invalidated', 'Run Calculate again before reporting'],
              ]}
            />
            <Callout type="important">Never reuse an old result table after changing an input. Confirm that inputs, results, diagram and PDF belong to the same calculation run.</Callout>
          </section>

          {/* 14 — Profile */}
          <section id="profile">
            <SectionTitle id="profile" icon={User}>14. Profile, Settings & Account Management</SectionTitle>
            <Screenshot src="/manual/en/15_profile_and_usage.png" alt="Profile Page" caption="Calculation/PDF credits, lashing display format, PDF theme and company profile" />
            <SubTitle>Profile Page</SubTitle>
            <Ul items={[
              'Account email and registration details',
              'Calculation usage history',
              'PDF report archive — download any past report',
              'Company logo upload for branded PDF reports (Professional / Enterprise plans)',
            ]} />
            <SubTitle>Language & Theme</SubTitle>
            <Ul items={[
              'Theme toggle (🌙/☀️ in header): light ↔ dark mode',
              'Language selector (🌐): English, Turkish, Spanish, German, Chinese, Japanese, Korean',
            ]} />
          </section>

          {/* FAQ */}
          <section id="faq">
            <SectionTitle id="faq" icon={LifeBuoy}>FAQ & Troubleshooting</SectionTitle>
            <div className="space-y-4">
              {[
                { q: 'The Calculate button is greyed out.', a: 'A required field is empty or invalid. Confirm Weight, Dimensions, Transport Mode, Unit, and at least one rope type are all set.' },
                { q: 'Loop Lashing checkbox is disabled.', a: 'The Oversized Width flag is enabled. Disable it if cargo doesn\'t actually overhang the unit sides.' },
                { q: 'The Stopper option is not appearing.', a: 'Stoppers are only available with Vessel or Mafi transport units in Sea mode combinations.' },
                { q: 'Wooden Dunnage is not appearing.', a: 'Dunnage is not available for Vessel transport units. Use stoppers instead.' },
                { q: 'Results show red in all directions.', a: 'Add more ropes, use higher MSL rope types, enable loop lashing, or add dunnage/stoppers.' },
                { q: 'Guest upload link shows "Invalid or Expired".', a: 'The token may have been revoked. Go to /surveys and copy a fresh link from the report card.' },
                { q: 'The 3D view shows a blank/black canvas.', a: 'Try a Chromium-based browser (Chrome, Edge, Brave) with hardware acceleration enabled in browser settings.' },
                { q: 'How do I put my company logo on PDF reports?', a: 'Company branding is available on Professional and Enterprise plans. Upload your logo in /profile under Company Settings.' },
                { q: 'Can I use this platform offline?', a: 'The calculation engine requires a backend API connection. Ensure a stable internet connection for active use.' },
              ].map(({ q, a }, i) => (
                <details key={i} className="group border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden">
                  <summary className="flex justify-between items-center px-5 py-4 cursor-pointer font-semibold text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors list-none">
                    <span>{q}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-5 pb-4 text-sm text-slate-600 dark:text-slate-300 border-t border-slate-100 dark:border-white/10 pt-3">{a}</div>
                </details>
              ))}
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-slate-100 dark:border-white/10 pt-8 text-center text-xs text-slate-400">
            <p>Lashing Calculator User Manual — Version 2.4.2</p>
            <p className="mt-1">© 2026 LashingPlan.com | CTU Code (IMO/ILO/UNECE) Simplified Assessment Method</p>
          </div>

        </main>
      </div>
    </div>
  );
};

export const UserManualPage: React.FC = () => {
  const { lang } = useLanguage();
  return lang === 'tr' ? <UserManualTurkish /> : <EnglishUserManualPage />;
};

export default UserManualPage;
